import React, { useRef, useState, useEffect } from 'react';
import { Container, Row, Col, Navbar, Button, Form, Tabs, Tab, Modal } from 'react-bootstrap';
import ChannelList from './ChannelList';
import AuxSendList from './AuxSendList';
import { parseSnapshot } from './parse';
import { generateSnapshot } from './generate';
import AdBanner from './AdBanner';

export default function App() {
  const fileInputRef = useRef();
  const [filename, setFilename] = useState('');
  const [raw, setRaw] = useState('');
  const [parsed, setParsed] = useState(null);
  const [channelsState, setChannelsState] = useState({});
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  useEffect(() => {
    try {
      const seen = localStorage.getItem('uisnapshot_disclaimer');
      if (!seen) setShowDisclaimer(true);
    } catch (err) {}
  }, []);

  function openFile(e) {
    const f = e.target.files[0];
    if (!f) return;
    setFilename(f.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      setRaw(text);
      const p = parseSnapshot(text);
      setParsed(p);
      setChannelsState(p.channels);
    };
    reader.readAsText(f);
  }

  function handleReorder(category, next) {
    setChannelsState((s) => ({ ...s, [category]: next }));
  }

  function getEntryValue(category, index, rest) {
    if (!parsed || !parsed.entries) return '';
    let e;
    if (index == null) {
      e = parsed.entries.find((en) => en.category === category && en.rest === rest);
    } else {
      e = parsed.entries.find((en) => en.category === category && en.index === index && en.rest === rest);
    }
    return e ? e.value : '';
  }

  function setEntryValue(category, index, rest, value) {
    if (!parsed) return;
    setParsed((p) => {
      const entries = p.entries.slice();
      const lines = p.lines.slice();
      let found = false;
      for (let i = 0; i < entries.length; i++) {
        const en = entries[i];
        if (en.category === category && en.index === index && en.rest === rest) {
          entries[i] = { ...en, value: String(value) };
          // Also update the underlying line so non-remapped entries (e.g. master) are saved correctly
          const updatedLine = index != null
            ? `${category}.${index}.${rest}=${value}`
            : `${category}.${rest}=${value}`;
          lines[en.lineIndex] = updatedLine;
          found = true;
          break;
        }
      }
      if (!found) {
        // append new entry line at end
        const newLine = index != null
          ? `${category}.${index}.${rest}=${value}`
          : `${category}.${rest}=${value}`;
        lines.push(newLine);
        entries.push({ lineIndex: lines.length - 1, line: newLine, category, index, rest, value: String(value) });
      }
      return { ...p, entries, lines };
    });

    // If changing a channel name, also update channelsState so UI updates immediately
    if (rest === 'name') {
      setChannelsState((s) => {
        const arr = (s[category] || []).map((item) => (item.index === index ? { ...item, name: String(value) } : item));
        return { ...s, [category]: arr };
      });
    }
  }

  function buildIndexMapping() {
    const mapping = {};
    for (const cat of Object.keys(channelsState)) {
      const arr = channelsState[cat];
      mapping[cat] = {};
      for (let newPos = 0; newPos < arr.length; newPos++) {
        const item = arr[newPos];
        mapping[cat][item.index] = newPos;
      }
    }
    return mapping;
  }

  function saveFile() {
    if (!parsed) return;
    const mapping = buildIndexMapping();
    const out = generateSnapshot(parsed.lines, parsed.entries, mapping);
    const blob = new Blob([out], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'modified.uisnapshot';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  // export all parsed entries for a given channel (category + index)
  function exportChannelEntries(category, index) {
    if (!parsed || !parsed.entries) return {};
    const out = {};
    for (const en of parsed.entries) {
      if (en.category === category && en.index === index) {
        out[en.rest] = en.value;
      }
    }
    return out;
  }

  // import a map of rest->value into a channel using setEntryValue
  function importChannelEntries(category, index, map) {
    if (!map || !setEntryValue) return;
    for (const k of Object.keys(map)) {
      try { setEntryValue(category, index, k, map[k]); } catch (err) {}
    }
  }

  return (
    <div className="app-root">
      <Navbar className="mb-3">
        <Container>
          <Navbar.Brand>UISnapshot Editor</Navbar.Brand>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: '#aaa' }}>{filename || 'Open a snapshot file:'}</span>
            <input ref={fileInputRef} type="file" accept=".uisnapshot,.snapshot,.txt" onChange={openFile} style={{ display: 'none' }} />
            <Button variant="outline-primary" onClick={() => fileInputRef.current.click()} className="me-2">Open</Button>
            <Button variant="primary" onClick={saveFile} disabled={!parsed}>Save</Button>
          </div>
        </Container>
      </Navbar>

      <Modal show={showDisclaimer} onHide={() => {}} backdrop="static" keyboard={false} className="disclaimer-modal">
        <Modal.Header>
          <Modal.Title>Disclaimer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p><strong>About this tool</strong></p>
          <p>This application allows you to view and modify snapshot files exported from SoundCraft Ui12 and Ui16 mixers.</p>

          <p><strong>Where to export/import snapshots</strong></p>
          <p>On your mixer, open <em>Settings &gt; Shows</em>. Use the <em>Export</em> and <em>Import</em> controls to download or upload snapshot files.</p>

          <p><strong>Reordering channels</strong></p>
          <p>You can change channel order by dragging channel names in the <em>Inputs</em> and <em>Auxes</em> tabs. Changes are applied to the snapshot when you save.</p>

          <p><strong>Liability and risk</strong></p>
          <p>Use this tool at your own risk. Hallonsoft provides this software "as is" and is not responsible for any loss, damage, or other consequences that may result from its use. Always keep backups of your original snapshot files before making changes.</p>

          <Form.Check type="checkbox" label="Don't show this again" checked={dontShowAgain} onChange={(e) => setDontShowAgain(e.target.checked)} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => {
            try { if (dontShowAgain) localStorage.setItem('uisnapshot_disclaimer', '1'); } catch (err) {}
            setShowDisclaimer(false);
          }}>Close</Button>
        </Modal.Footer>
      </Modal>

      <div className="app-main">
        <div className="app-content-wrapper">
          <div className="app-content">
          <Container>

        <Row className="mt-3 flex-grow-1">
          <Col className="h-100">
            <Tabs defaultActiveKey="inputs" id="channel-tabs" className="mb-3">
              <Tab eventKey="inputs" title="Inputs">
                      {channelsState.i ? (
                      <ChannelList category={'i'} channels={channelsState.i} onReorder={handleReorder} getEntryValue={getEntryValue} setEntryValue={setEntryValue} exportChannel={exportChannelEntries} importChannel={importChannelEntries} groups={channelsState.s || []} />
                ) : (
                  <div>No inputs found</div>
                )}
              </Tab>
              <Tab eventKey="aux" title="Auxes">
                {channelsState.a ? (
                  <Tabs defaultActiveKey="general" id="aux-sub-tabs" className="mb-2">
                    <Tab eventKey="general" title="General">
                      <ChannelList category={'a'} channels={channelsState.a} onReorder={handleReorder} getEntryValue={getEntryValue} setEntryValue={setEntryValue} exportChannel={exportChannelEntries} importChannel={importChannelEntries} groups={channelsState.s || []} />
                    </Tab>
                    {channelsState.a.map((auxCh) => (
                      <Tab key={auxCh.index} eventKey={`aux-ch-${auxCh.index}`} title={auxCh.name || `Aux ${auxCh.index + 1}`}>
                        <AuxSendList
                          auxChannel={auxCh}
                          inputChannels={channelsState.i || []}
                          getEntryValue={getEntryValue}
                          setEntryValue={setEntryValue}
                        />
                      </Tab>
                    ))}
                  </Tabs>
                ) : (
                  <div>No aux/outputs found</div>
                )}
              </Tab>
              <Tab eventKey="groups" title="Groups">
                {channelsState.s ? (
                  <ChannelList category={'s'} channels={channelsState.s} onReorder={handleReorder} getEntryValue={getEntryValue} setEntryValue={setEntryValue} exportChannel={exportChannelEntries} importChannel={importChannelEntries} groups={channelsState.s || []} />
                ) : (
                  <div>No subgroups found</div>
                )}
              </Tab>
            </Tabs>
          </Col>
        </Row>
      </Container>
          </div>
          <AdBanner />
        </div>
      </div>
    </div>
  );
}
